import * as Sentry from "@sentry/react";

export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

interface LogContext {
  [key: string]: any;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: string;
  userAgent?: string;
  url?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private formatLogEntry(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): LogEntry {
    return {
      level,
      message,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
  }

  private logToConsole(entry: LogEntry) {
    const style = this.getLogStyle(entry.level);
    const prefix = `[${entry.level.toUpperCase()}]`;

    if (this.isDevelopment) {
      console.log(
        `%c${prefix} ${entry.message}`,
        style,
        entry.context || ""
      );
    }
  }

  private getLogStyle(level: LogLevel): string {
    const styles = {
      [LogLevel.DEBUG]: "color: #6B7280; font-weight: normal;",
      [LogLevel.INFO]: "color: #3B82F6; font-weight: bold;",
      [LogLevel.WARN]: "color: #F59E0B; font-weight: bold;",
      [LogLevel.ERROR]: "color: #EF4444; font-weight: bold;",
    };
    return styles[level];
  }

  debug(message: string, context?: LogContext) {
    const entry = this.formatLogEntry(LogLevel.DEBUG, message, context);
    this.logToConsole(entry);
  }

  info(message: string, context?: LogContext) {
    const entry = this.formatLogEntry(LogLevel.INFO, message, context);
    this.logToConsole(entry);
    
    Sentry.addBreadcrumb({
      message,
      level: "info",
      data: context,
    });
  }

  warn(message: string, context?: LogContext) {
    const entry = this.formatLogEntry(LogLevel.WARN, message, context);
    this.logToConsole(entry);
    
    Sentry.addBreadcrumb({
      message,
      level: "warning",
      data: context,
    });
    
    Sentry.captureMessage(message, {
      level: "warning",
      contexts: { custom: context },
    });
  }

  error(message: string, error?: Error | unknown, context?: LogContext) {
    const entry = this.formatLogEntry(LogLevel.ERROR, message, context);
    this.logToConsole(entry);
    
    if (error instanceof Error) {
      console.error(error);
      Sentry.captureException(error, {
        contexts: {
          custom: {
            message,
            ...context,
          },
        },
      });
    } else {
      Sentry.captureMessage(message, {
        level: "error",
        contexts: { custom: { error, ...context } },
      });
    }
  }

  // Performance logging
  startTimer(label: string): () => void {
    const start = performance.now();
    this.debug(`Timer started: ${label}`);
    
    return () => {
      const duration = performance.now() - start;
      this.info(`Timer ended: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      
      Sentry.addBreadcrumb({
        message: `Performance: ${label}`,
        level: "info",
        data: { duration },
      });
    };
  }

  // Audit logging for important actions
  audit(action: string, context?: LogContext) {
    this.info(`AUDIT: ${action}`, context);
    
    Sentry.addBreadcrumb({
      category: "audit",
      message: action,
      level: "info",
      data: context,
    });
  }

  // API request logging
  apiRequest(method: string, url: string, context?: LogContext) {
    this.debug(`API Request: ${method} ${url}`, context);
    
    Sentry.addBreadcrumb({
      category: "http",
      type: "http",
      data: {
        method,
        url,
        ...context,
      },
    });
  }

  apiResponse(method: string, url: string, status: number, context?: LogContext) {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;
    const entry = this.formatLogEntry(
      level,
      `API Response: ${method} ${url} - ${status}`,
      context
    );
    this.logToConsole(entry);
    
    Sentry.addBreadcrumb({
      category: "http",
      type: "http",
      data: {
        method,
        url,
        status_code: status,
        ...context,
      },
    });
  }

  // User action logging
  userAction(action: string, context?: LogContext) {
    this.info(`User Action: ${action}`, context);
    
    Sentry.addBreadcrumb({
      category: "user",
      message: action,
      level: "info",
      data: context,
    });
  }

  // Set user context for error tracking
  setUser(userId: string, email?: string, username?: string) {
    Sentry.setUser({
      id: userId,
      email,
      username,
    });
    this.info("User context set", { userId, email });
  }

  // Clear user context
  clearUser() {
    Sentry.setUser(null);
    this.info("User context cleared");
  }

  // Set custom context
  setContext(key: string, context: LogContext) {
    Sentry.setContext(key, context);
  }
}

export const logger = new Logger();
